/**
 * /venues/:venueSlug/tournaments — every night this venue runs, and the way in.
 *
 * Public. It took over this route from PublicTournaments.jsx, which listed the
 * same nights and could do nothing with them: registering was a placeholder, and
 * AccountPrompt had been built for a page that did not exist yet. This is that
 * page, so the list and the act of entering it are one screen rather than a list
 * that sends you somewhere else to do the thing you came for.
 *
 * TWO TABS, because the two halves answer different questions. Upcoming is "can
 * I play", and every row is a control. History is "what happened", and every row
 * is a link into the drill-down.
 *
 * TWO COLUMNS INSIDE EACH TAB, standalone against season-tied, because a season's
 * nights are a numbered sequence and a one-off is an event. A side with nothing
 * in it collapses rather than showing an empty heading — "Season" over a blank
 * column reads as a loading failure.
 *
 * WHAT THE SERVER DECIDES, NOT THIS PAGE. Whether the caller is registered comes
 * back on each row as `me`, so the buttons are a render of the server's answer
 * rather than a guess this page maintains. The rules the buttons follow are the
 * server's too: unregistering closes when registration does (entries/[entryId]
 * answers 409 after that), and the waitlist opens only when a tournament is full
 * and still in registration (waitlist/index refuses otherwise). Both are mirrored
 * here so a player is not offered a control that will refuse them — never
 * INSTEAD of the server's check.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiDel } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useVenuePage } from '../lib/useVenuePage.js';
import { formatDateShort } from '../lib/format.js';
import { tournamentLabel } from '../lib/placement.js';
import StatusBadge from '../components/StatusBadge.jsx';
import AccountPrompt from '../components/auth/AccountPrompt.jsx';

/** Anything not yet completed is still ahead of you. */
const UPCOMING = new Set(['upcoming', 'registration', 'active']);

/**
 * What a tournament is called.
 *
 * THERE IS NO NAME COLUMN. `tournaments` carries night_number and a date and
 * nothing else a human would call a title, so a night IS its number — which is
 * why tournamentLabel falls back to a bare 'Tournament' rather than to a name
 * that was never stored. PublicTournaments named them the same way.
 */
const nameOf = (t) => tournamentLabel(t.night_number);

/* -------------------------------------------------------------------------- */
/*  One upcoming tournament                                                    */
/* -------------------------------------------------------------------------- */

function UpcomingRow({ venueId, tournament: t, isAuthenticated, onChanged, onPrompt }) {
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [notice, setNotice]   = useState(null);

  const full      = t.entry_count >= t.max_entries;
  const open      = t.status === 'registration';
  const me        = t.me;
  const path      = `/api/venues/${venueId}/tournaments/${t.id}`;

  const run = async (call) => {
    setBusy(true);
    setError(null);
    const { data, error: err } = await call();
    setBusy(false);

    if (err) { setError(err); return null; }
    await onChanged();
    return data;
  };

  const register = () => run(() => apiPost(`${path}/entries`, {}));

  const joinWaitlist = async () => {
    const data = await run(() => apiPost(`${path}/waitlist`, {}));
    if (!data) return;
    // The server says whether it actually sent anything, so the page can promise
    // an email only when one went out.
    setNotice(data.emailed
      ? `You're #${data.position} on the waitlist — we've emailed you.`
      : `You're #${data.position} on the waitlist.`);
  };

  const unregister = async () => {
    setConfirm(false);
    await run(() => apiDel(`${path}/entries/${me.entryId}`));
  };

  return (
    <div className="hd-drill-row hd-entry-row">
      <span className="hd-drill-date">{formatDateShort(t.date) ?? '—'}</span>

      <span className="hd-drill-name">
        {nameOf(t)}
        {t.season_name && <span className="hd-muted"> · {t.season_name}</span>}
        <span className="hd-entry-count"> {t.entry_count}/{t.max_entries}</span>
      </span>

      <StatusBadge status={t.status} />

      <span className="hd-entry-action">
        {/* Signed out: every route in leads through the same prompt, so the
            account-or-guest decision is made once, here, rather than on a
            separate page that has to be navigated back from. */}
        {!isAuthenticated && open && !full && (
          <button type="button" className="hd-btn hd-btn-primary hd-btn-sm" onClick={onPrompt}>
            Register
          </button>
        )}

        {/* NO WAITLIST BUTTON WHILE SIGNED OUT, deliberately. AccountPrompt's
            guest path posts to /entries and nothing else, so offering the
            waitlist here would walk a guest into a 409 'Tournament is full' from
            a button that promised them a place in the queue. The waitlist
            endpoint does accept guests — it is GuestRegisterForm that only knows
            one target — so this is a gap to close in that component rather than
            a limit of the API. */}
        {!isAuthenticated && open && full && (
          <span className="hd-muted">
            Full — <Link to="/login">sign in</Link> to join the waitlist
          </span>
        )}

        {isAuthenticated && me?.status === 'registered' && open && (
          <button
            type="button"
            className="hd-btn hd-btn-quiet hd-btn-sm"
            onClick={() => setConfirm(true)}
            disabled={busy}
          >
            Unregister
          </button>
        )}

        {/* Registered, but the draw has been made. No control at all rather than
            a disabled one: the server would refuse it, and a greyed button
            invites a click that can only fail. */}
        {isAuthenticated && me?.status === 'registered' && !open && (
          <span className="hd-muted">Entered</span>
        )}

        {isAuthenticated && me?.status === 'waitlisted' && (
          <span className="hd-muted">Waitlisted #{me.position}</span>
        )}

        {isAuthenticated && !me && open && !full && (
          <button
            type="button"
            className="hd-btn hd-btn-primary hd-btn-sm"
            onClick={register}
            disabled={busy}
          >
            {busy ? 'Registering…' : 'Register'}
          </button>
        )}

        {isAuthenticated && !me && open && full && (
          <button
            type="button"
            className="hd-btn hd-btn-quiet hd-btn-sm"
            onClick={joinWaitlist}
            disabled={busy}
          >
            {busy ? 'Joining…' : 'Join waitlist'}
          </button>
        )}

        {!me && !open && <span className="hd-muted">{full ? 'Full' : 'Closed'}</span>}
      </span>

      {notice && <p className="hd-hint hd-entry-note">{notice}</p>}

      {/* The badge-danger + modal-error pair is how Pair.jsx renders an inline
          failure: the badge carries the colour, hd-modal-error lets it wrap
          rather than stretching a row. */}
      {error && (
        <div className="hd-badge hd-badge-danger hd-modal-error hd-entry-note" role="alert">
          {error}
        </div>
      )}

      {/* Inline rather than a browser confirm: the warning below is the whole
          point of asking, and window.confirm cannot carry it. */}
      {confirm && (
        <div className="hd-danger-panel hd-entry-confirm">
          <strong>Unregister from {nameOf(t)}?</strong>

          <p className="hd-hint" style={{ marginBottom: 0 }}>
            {me?.paid
              ? 'You have paid for this entry. Unregistering will NOT automatically '
                + 'refund you — speak to the venue about getting your money back.'
              : 'Your spot will be offered to the next player on the waitlist.'}
          </p>

          <div className="hd-row">
            <button
              type="button"
              className="hd-btn hd-btn-danger hd-btn-sm"
              onClick={unregister}
              disabled={busy}
            >
              {busy ? 'Removing…' : 'Yes, unregister'}
            </button>
            <button
              type="button"
              className="hd-btn hd-btn-quiet hd-btn-sm"
              onClick={() => setConfirm(false)}
              disabled={busy}
            >
              Keep my spot
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  One completed tournament                                                   */
/* -------------------------------------------------------------------------- */

function HistoryRow({ venueSlug, tournament: t }) {
  return (
    <Link
      className="hd-drill-row hd-drill-link"
      to={`/venues/${venueSlug}/tournaments/${t.id}/results`}
    >
      <span className="hd-drill-date">{formatDateShort(t.date) ?? '—'}</span>

      <span className="hd-drill-name">
        {nameOf(t)}
        {t.season_name && <span className="hd-muted"> · {t.season_name}</span>}
      </span>

      {/* Absent on a night that was completed without stat rows — a seeded or
          hand-corrected tournament. A dash is honest; inventing a winner from
          the standings would not be. */}
      <span className="hd-history-result">
        {t.winner
          ? <>🏆 {t.winner.displayName}
              {t.runnerUp && <span className="hd-muted"> beat {t.runnerUp.displayName}</span>}
            </>
          : <span className="hd-muted">No result recorded</span>}
      </span>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*  The two-column split                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Standalone on the left, season-tied on the right — unless one side is empty,
 * in which case the other becomes a single centred list with no heading. A
 * heading over nothing reads as something that failed to load.
 */
function SplitColumns({ standalone, seasonal, renderRow, emptyNote }) {
  const hasStandalone = standalone.length > 0;
  const hasSeasonal   = seasonal.length > 0;

  if (!hasStandalone && !hasSeasonal) {
    return <div className="hd-empty">{emptyNote}</div>;
  }

  if (hasStandalone !== hasSeasonal) {
    return (
      <div className="hd-entry-single">
        <div className="hd-drill-list">
          {(hasStandalone ? standalone : seasonal).map(renderRow)}
        </div>
      </div>
    );
  }

  return (
    <div className="hd-entry-split">
      <section className="hd-stats-section">
        <h2 className="hd-section-title">Tournaments</h2>
        <div className="hd-drill-list">{standalone.map(renderRow)}</div>
      </section>

      <section className="hd-stats-section">
        <h2 className="hd-section-title">Season</h2>
        <div className="hd-drill-list">{seasonal.map(renderRow)}</div>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export default function TournamentRegister() {
  const { venueSlug } = useParams();
  const { isAuthenticated } = useAuth();

  const [tab, setTab] = useState('upcoming');
  // Which tournament the signed-out prompt is for. Null when it is closed.
  const [prompt, setPrompt] = useState(null);

  const state = useVenuePage(
    venueSlug,
    (venue) => apiGet(`/api/venues/${venue.id}/tournaments`),
    // isAuthenticated is a dependency because the response DEPENDS ON IT: `me`
    // only comes back for a caller the server recognises, so signing in has to
    // refetch or the buttons stay anonymous until a reload.
    [isAuthenticated]
  );

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack hd-stats-page">
        <h1 className="hd-page-title">
          {state.venue ? 'Tournaments unavailable' : 'Venue not found'}
        </h1>
        <p className="hd-muted">
          {state.venue ? state.error : <>No venue at <code>/{venueSlug}</code>.</>}
          {' '}
          <Link to={state.venue ? `/venues/${venueSlug}` : '/'}>
            {state.venue ? 'Back to the venue' : 'Back to the home page'}
          </Link>
        </p>
      </div>
    );
  }

  const { venue, data } = state;
  const all = data.tournaments ?? [];

  const shown = all.filter((t) =>
    tab === 'upcoming' ? UPCOMING.has(t.status) : t.status === 'completed');

  const standalone = shown.filter((t) => t.season_id == null);
  const seasonal   = shown.filter((t) => t.season_id != null);

  const reload = state.reload;

  const renderRow = (t) => (tab === 'upcoming'
    ? (
      <UpcomingRow
        key={t.id}
        venueId={venue.id}
        tournament={t}
        isAuthenticated={isAuthenticated}
        onChanged={reload}
        onPrompt={() => setPrompt(t)}
      />
    )
    : <HistoryRow key={t.id} venueSlug={venue.slug} tournament={t} />
  );

  return (
    <div className="hd-stack hd-entry-page">
      <div className="hd-stats-head">
        <h1 className="hd-page-title">Tournaments</h1>
        <p className="hd-meta">
          <Link to={`/venues/${venue.slug}`}>{venue.name}</Link>
        </p>
      </div>

      <div className="hd-tabs hd-entry-tabs" role="tablist">
        {[['upcoming', 'Upcoming'], ['history', 'History']].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`hd-tab ${tab === id ? 'hd-tab-active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <SplitColumns
        standalone={standalone}
        seasonal={seasonal}
        renderRow={renderRow}
        emptyNote={tab === 'upcoming'
          ? 'No tournaments coming up — check back soon.'
          : 'No completed tournaments yet.'}
      />

      {prompt && (
        <AccountPrompt
          venueSlug={venue.slug}
          venueId={venue.id}
          tournamentId={prompt.id}
          tournamentLabel={nameOf(prompt)}
          onClose={() => setPrompt(null)}
          onGuestRegistered={() => { setPrompt(null); reload(); }}
        />
      )}
    </div>
  );
}
